import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { DataList, PageHeader, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Plus } from "lucide-react";
import { Button, Section, StateView, useAuth, When } from "@ozpanel/ui";
import { useCreate } from "../../useCreate";
import { useAccountsApi } from "../../accountsApi";

interface Domain {
  id: number;
  tenant_id: number;
  fqdn: string;
  status: string;
  kind: string;
  created_at: string;
}

const columns: DataListColumn<Domain>[] = [
  { key: "fqdn", label: "Domain", sortable: true },
  {
    key: "kind",
    label: "Kind",
    sortable: true,
    render: (d) => <StatusPill tone={d.kind === "apex" ? "ok" : "neutral"}>{d.kind}</StatusPill>,
  },
  {
    key: "status",
    label: "Status",
    sortable: true,
    render: (d) => <StatusPill tone={d.status === "active" ? "ok" : "warn"}>{d.status}</StatusPill>,
  },
  { key: "created_at", label: "Added", sortable: true, render: (d) => <When value={d.created_at} /> },
];

export function DomainsScreen() {
  const { client } = useAuth();
  const api = useAccountsApi();
  const tenants = useQuery({ queryKey: ["tenants"], queryFn: api.listTenants, retry: false });
  const create = useCreate({
    title: "Add a domain",
    description:
      "Provisions DNS, a web vhost and mail routing for this name. Existing records for the name are adopted, not replaced.",
    submitLabel: "Add domain",
    fields: [
      { key: "fqdn", label: "Domain name", required: true, placeholder: "example.com" },
      {
        key: "tenant_id",
        label: "Account",
        required: true,
        options: (tenants.data?.items ?? []).map((t) => ({ value: String(t.id), label: t.name })),
      },
    ],
    run: (v) => api.createDomain({ fqdn: v.fqdn, tenant_id: Number(v.tenant_id) }),
    invalidates: [["domains"], ["tenants"]],
    success: (v) => `${v.fqdn} added`,
  });
  const domains = useQuery({
    queryKey: ["domains"],
    queryFn: () => client.apiFetch<{ items: Domain[]; total: number }>("/domains"),
  });

  const [open, setOpen] = React.useState<string | null>(null);

  return (
    <div>
      <PageHeader
        title="Domains"
        subtitle={domains.data ? `${domains.data.total} across all accounts` : undefined}
        actions={
          <Button variant="accent" onClick={create.open}>
            <Plus size={14} />
            Add domain
          </Button>
        }
      />
      <DataList
        items={domains.data?.items ?? []}
        columns={columns}
        getRowKey={(d) => String(d.id)}
        searchable
        searchPlaceholder="Search domains…"
        loading={domains.isLoading}
        error={domains.error}
        errorContext="domains"
        emptyMessage="No domains yet."
        emptyHint="Domains are added to an account, then provisioned across DNS, web and mail."
        expandedKey={open}
        renderExpanded={(d) => <DomainOverview fqdn={d.fqdn} />}
        rowActions={(d) => (
          <Button size="sm" variant="ghost" onClick={() => setOpen(open === String(d.id) ? null : String(d.id))}>
            {open === String(d.id) ? "Close" : "What it runs"}
          </Button>
        )}
      />
      {create.dialog}
    </div>
  );
}

// Everything this domain has provisioned across the platform, grouped by the module that owns it.
// The list can say a domain is "active"; only this says whether it actually has a website, mail,
// DNS and a PHP pool behind that word.
function DomainOverview({ fqdn }: { fqdn: string }) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["domain-overview", fqdn],
    queryFn: () =>
      client.apiFetch<{
        count?: number;
        default_docroot?: string;
        status?: string;
        // How the domain is served — the governing fact for whether a "files <docroot>" label
        // means anything. A proxy/app domain reverse-proxies to a container and has no file root.
        website?: { mode?: string; upstream?: string } | null;
        by_module?: Record<string, number>;
        resources?: { module?: string; kind?: string; name?: string; detail?: string }[] | null;
      }>(`/domains/${encodeURIComponent(fqdn)}/overview`),
    retry: false,
  });

  return (
    <StateView loading={q.isLoading} error={q.error} errorContext="what this domain runs">
      <div className="space-y-2 text-[12.5px]">
        <div className="flex flex-wrap gap-3 text-muted">
          {Object.entries(q.data?.by_module ?? {}).map(([m, n]) => (
            <span key={m}>{m} <span className="font-mono text-fg">{n}</span></span>
          ))}
          {(() => {
            const mode = (q.data?.website?.mode ?? "").toLowerCase();
            // A proxy/app domain has no file docroot — say what it proxies to instead of a
            // "files <path>" label that reads as "serves files from here".
            if (mode === "proxy" || mode === "app") {
              return <span>proxied to <span className="font-mono text-fg">{q.data?.website?.upstream || "an app"}</span></span>;
            }
            // php/static (or a domain with no website yet) can legitimately have a docroot.
            if (q.data?.default_docroot) {
              return <span>files <span className="font-mono text-fg">{q.data.default_docroot}</span></span>;
            }
            return null;
          })()}
        </div>
        {(q.data?.resources ?? []).length === 0 ? (
          <div className="text-muted">Nothing is provisioned for this domain yet.</div>
        ) : (
          <ul className="space-y-0.5">
            {(q.data?.resources ?? []).map((r, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-2 border-b border-line py-1">
                <span className="w-16 shrink-0 text-[11px] uppercase tracking-wide text-muted">{r.module}</span>
                <span className="font-mono text-[12px]">{r.name}</span>
                {r.kind && <span className="text-[11px] text-muted">{r.kind}</span>}
                {r.detail && <span className="text-[11px] text-muted">{r.detail}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </StateView>
  );
}
